import cx from 'classnames'
import { Fragment, useEffect, useRef } from 'react'
import { ContextMenuTrigger } from 'almanac-ui/ContextMenuTrigger/ContextMenuTrigger'
import Styles from './ContextMenus.module.scss'
import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import { ActionKey, BaseContextMenuProps } from './Types'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { VisualSwitch } from 'components/VisualSwitch/VisualSwitch'

import { LocationPicker, LocationPickerFolder, SelectedFolder } from 'components/LocationPicker/LocationPicker'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useRootStore } from 'stores/RootStore'
import { FolderClient } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { getAllDocsPath, getFolderPath } from 'utils/urlUtils'
import { showSuccessfulMoveToast } from 'common/ToastMessage/showMoveToast'
import { moveError } from 'components/NavRightClickMenu/components/MoveError/MoveError'
import { HandbookTreeItem } from '../Stores/HandbookTreeItem'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useDispatch, useSelector } from 'react-redux'
import { setMoveModalVisible } from 'redux/actions'
import { RootState } from 'types'
import { isEqual } from 'lodash'
import { useEvaluateMove } from 'components/LocationPicker/hooks/useEvaluateMove'

export const BaseContextMenu = ({
  children,
  menus,
  handleAction,
  visible,
  setVisible,
  disabledKeys,
  item,
}: BaseContextMenuProps) => {
  const dispatch = useDispatch()
  const moveModalVisible = useSelector(({ moveModalVisible }: RootState) => moveModalVisible, isEqual)
  const workspaceId = useCurrentWorkspaceId()
  const rootStore = useRootStore()
  const { evaluateMove } = useEvaluateMove()

  let selectedFiles = []
  let treeItem: HandbookTreeItem | undefined = undefined
  let currentFolder: LocationPickerFolder | null = null

  if (item) {
    treeItem = rootStore.handbooks.items.get(item.id.toString())

    if (treeItem) {
      selectedFiles = treeItem.data.document ? [treeItem.data.document] : [treeItem.data.folder]
      const folder = treeItem.parentHandbook?.handbook.folder

      if (folder) {
        currentFolder = {
          id: folder.key,
          title: folder.title,
          ancestor_keys: folder.almanac_file.ancestors.map((a) => a.id),
        }
      }
    }
  }

  const moveFiles = async ({
    selectedFolder,
    newWorkspaceId,
  }: {
    selectedFolder: SelectedFolder | null
    newWorkspaceId: number
  }) => {
    if (!item || !treeItem) return

    const numFiles = selectedFiles.length
    const selectedFolderId = !!selectedFolder ? selectedFolder.id : null
    const folderKeys: string[] = item.folder ? [item.folder.key] : []
    const articleKeys: string[] = item.document ? [item.document.key] : []

    const dismissLoadingToast = showToastMessage('Moving...')
    const response = await FolderClient.bulkMoveFiles({
      folderIds: folderKeys,
      articleIds: articleKeys,
      fileIds: [],
      destinationFolderId: selectedFolderId,
      destinationWorkspaceId: newWorkspaceId,
      originalWorkspaceId: workspaceId,
    })
    dismissLoadingToast()

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      const url = selectedFolder?.seo_string
        ? getFolderPath({ folderSlug: selectedFolder.seo_string })
        : getAllDocsPath(newWorkspaceId)

      dispatch(setMoveModalVisible(false))
      showSuccessfulMoveToast(numFiles, url, selectedFolder?.title)

      if (item.folder) rootStore.files.fetch(item.folder.key, true)
      rootStore.handbooks.refresh(treeItem.parentHandbook?.handbook?.key)
      rootStore.handbooks.refresh(selectedFolder?.handbook_key)
      return
    }
    if (response.type === 'error') {
      moveError({ status: response.status, data: response.data })
    }
    dispatch(setMoveModalVisible(false))
  }

  const dropdownComponents = () => {
    if (moveModalVisible && item) {
      return (
        <div className={Styles.filePicker}>
          <LocationPicker
            fileTitle={item.title}
            actionType="move"
            initialFolder={currentFolder}
            onSelectFolder={(payload) => {
              const folderIds: string[] = item.folder ? [item.folder.key] : []
              const articleIds: string[] = item.document ? [item.document.key] : []

              evaluateMove({
                onSelectFolderPayload: payload,
                fileMoveAttemptPayload: {
                  folderIds,
                  articleIds,
                  originalWorkspaceId: workspaceId,
                },
                handleMove: moveFiles,
              })
            }}
            initialWorkspaceId={workspaceId}
            isRootDisabled={!!item.document || !currentFolder}
            disabledFolderIds={item.folder ? [item.folder.key] : []}
          />
        </div>
      )
    }

    return (
      <div className={Styles.menuComponents}>
        {menus.map((menuItems, idx) => (
          <Fragment key={`menu-${idx}`}>
            {idx > 0 && <hr className={Styles.divider} />}
            <Menu
              aria-label="Menu"
              selectionMode="single"
              onAction={(key) => handleAction(key as ActionKey)}
              showCheckmark={false}
              itemClassName={Styles.menuItem}
              disabledKeys={disabledKeys}
            >
              {menuItems.map(({ Icon, key, ...itemProps }) => {
                const isDisabled = disabledKeys?.has(key)
                return (
                  <Item {...itemProps} key={key}>
                    <Tooltip label="Requires edit access" placement="right" tooltipState={{ isOpen: isDisabled }}>
                      <span>
                        <Icon aria-hidden="true" />
                        {itemProps.textValue}
                        {itemProps.canToggle && (
                          <div className={Styles.toggle}>
                            <VisualSwitch active={itemProps.isToggled || false} variant="sm" />
                          </div>
                        )}
                      </span>
                    </Tooltip>
                  </Item>
                )
              })}
            </Menu>
          </Fragment>
        ))}
      </div>
    )
  }

  const triggerRef = useRef<{ openMenu: (x: number, y: number) => void; closeMenu: () => void }>(null)

  useEffect(() => {
    if (!visible) {
      triggerRef.current?.closeMenu()
    }
  }, [visible])

  return (
    <ContextMenuTrigger
      ref={triggerRef}
      placement="bottom-end"
      popoverClassName={cx(Styles.contextMenuOverlay, {
        [Styles.filePickerOverlay]: moveModalVisible,
      })}
      onOpenChange={(isOpen) => {
        setVisible(isOpen)
        if (!isOpen) dispatch(setMoveModalVisible(false))
      }}
      onClose={() => dispatch(setMoveModalVisible(false))}
    >
      {children}
      {() => dropdownComponents()}
    </ContextMenuTrigger>
  )
}
